// 마을교육공동체(돌봄연계국 C) 전용 API 클라이언트.
//
// 격리 원칙: 기존 /design/api.ts 는 통합돌봄 CRUD 계약({resource: attrs})에 묶여 있다.
// Village 백엔드는 플랫 바디(top-level fields)를 쓰는 액션이 다수(transition, approve,
// safety_watches 등)라 별도 모듈로 둔다. 응답/에러 계약은 /design/api.ts 와 동일.

const BASE = "/api/v1/village";

function httpError(status: number, statusText: string, detail: any): Error {
  const err: any = new Error(`${status} ${statusText} ${JSON.stringify(detail)}`);
  err.status = status;
  err.code = detail?.error ?? null;
  err.serverMessage = detail?.message ?? null;
  err.details = detail?.details ?? null;
  err.body = detail ?? null;
  return err;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(BASE + path, {
    method,
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: "same-origin",
  });
  if (!res.ok) {
    let detail: unknown = null;
    try { detail = await res.json(); } catch { /* not JSON */ }
    throw httpError(res.status, res.statusText, detail);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

function qs(obj: Record<string, string | number | undefined | null>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null || v === "") continue;
    u.append(k, String(v));
  }
  const s = u.toString();
  return s ? "?" + s : "";
}

// ── 공통 타입 ───────────────────────────────────────────
export type BureauCode = "H" | "E" | "L" | "A" | "W" | "C" | "P" | "sponsor" | "endorse";
export type BureauKind = "bureau" | "support" | "endorse";

export interface Bureau {
  id: number;
  code: BureauCode;
  name: string;
  kind: BureauKind;
  summary: string;
  position: number;
  member_count?: number;
  pending_count?: number;
}

export interface Member {
  id: number;
  status: "pending" | "active" | "revoked";
  role_in_bureau: string | null;
  note: string | null;
  bureau: { id: number; code: BureauCode; name: string };
  user: { id: number; name: string; email: string; role: string };
  region_id: number | null;
  approved_by: string | null;
  approved_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

export interface Youth {
  id: number;
  name: string;
  phone: string | null;
  address: string | null;
  sub_area: string | null;
  region_id: number | null;
  birth_on: string | null;
  age: number | null;
  school_name: string | null;
  grade: number | null;
  track: "general" | "youth_council" | "care_subject";
  status: "active" | "inactive" | "withdrawn";
  latitude: number | null;
  longitude: number | null;
  created_at: string;
  guardians?: Guardian[];
}

export interface Guardian {
  id: number;
  youth_id: number;
  user_id: number;
  name: string;
  relation: string;
  primary: boolean;
  notify_consented: boolean;
}

export interface Program {
  id: number;
  title: string;
  summary: string | null;
  description: string | null;
  topic: string | null;
  status: "draft" | "recruiting" | "in_progress" | "completed" | "archived";
  capacity: number | null;
  min_grade: number | null;
  max_grade: number | null;
  recruit_start_on: string | null;
  recruit_end_on: string | null;
  start_on: string | null;
  end_on: string | null;
  location: string | null;
  latitude: number | null;
  longitude: number | null;
  bureau: { id: number; code: BureauCode; name: string };
  region_id: number | null;
  created_at: string;
  accepted_count?: number;
  applied_count?: number;
}

export interface Participant {
  id: number;
  program_id: number;
  role: "participant" | "teacher" | "mentor" | "parent";
  status: "applied" | "accepted" | "declined" | "attended" | "cancelled";
  note: string | null;
  youth: { id: number; name: string; grade: number | null } | null;
  user: { id: number; name: string; role: string } | null;
  approved_by: string | null;
  approved_at: string | null;
  created_at: string;
}

export interface SafetyWatch {
  id: number;
  user: { id: number; name: string };
  watchable_type: "Youth" | "Region" | "VillageProgram";
  watchable_id: number;
  status: "active" | "paused" | "ended";
  severity_hint: "low" | "normal" | "high";
  note: string | null;
  approved_by: string | null;
  approved_at: string | null;
  ended_at: string | null;
  created_at: string;
}

export interface Motion {
  id: number;
  title: string;
  description: string | null;
  category: string | null;
  status: "draft" | "open" | "closed" | "adopted" | "rejected";
  region_id: number | null;
  proposer_youth: { id: number; name: string } | null;
  voting_opens_at: string | null;
  voting_closes_at: string | null;
  created_at: string;
  yes_count?: number;
  no_count?: number;
  abstain_count?: number;
  total_votes?: number;
}

export interface Tally { yes: number; no: number; abstain: number; }

export interface Teacher {
  id: number;
  user: { id: number; name: string; email: string };
  bureau: { id: number; code: BureauCode; name: string };
  specialty: string | null;
  bio: string | null;
  statement: string | null;
  status: "pending" | "approved" | "suspended" | "expired";
  certified_on: string | null;
  expires_on: string | null;
  certified_by: string | null;
  expired: boolean;
  created_at: string;
}

export interface Curriculum {
  id: number;
  title: string;
  summary: string | null;
  outline: string | null;
  tags: string[];
  status: "draft" | "published" | "archived";
  region_id: number | null;
  bureau: { id: number; code: BureauCode; name: string } | null;
  min_grade: number | null;
  max_grade: number | null;
  session_count: number | null;
  published_at: string | null;
  archived_at: string | null;
  created_at: string;
}

export interface MapResource {
  id: number; name: string; category: string;
  lat: number; lng: number; address: string | null; region_id: number | null;
}

type ItemsResponse<T> = { items: T[] };

// ── 리소스 모듈 ─────────────────────────────────────────
export const bureaus = {
  list:  (kind?: BureauKind) => request<ItemsResponse<Bureau>>("GET", `/bureaus${qs({ kind })}`),
  get:   (idOrCode: number | BureauCode) => request<Bureau>("GET", `/bureaus/${idOrCode}`),
};

export const members = {
  list:   (opts: { status?: string; bureau_id?: number } = {}) =>
    request<ItemsResponse<Member>>("GET", `/members${qs(opts)}`),
  me:     () => request<ItemsResponse<Member>>("GET", "/members/me"),
  apply:  (attrs: { bureau_code: BureauCode; role_in_bureau?: string; region_id?: number; note?: string }) =>
    request<Member>("POST", "/members", attrs),
  approve: (id: number) => request<Member>("POST", `/members/${id}/approve`),
  revoke:  (id: number) => request<Member>("POST", `/members/${id}/revoke`),
};

export const youths = {
  list:   (opts: { track?: string; status?: string; q?: string } = {}) =>
    request<ItemsResponse<Youth>>("GET", `/youths${qs(opts)}`),
  get:    (id: number) => request<Youth>("GET", `/youths/${id}`),
  create: (attrs: Partial<Youth> & { name: string; track: string }) =>
    request<Youth>("POST", "/youths", attrs),
  update: (id: number, attrs: Partial<Youth>) =>
    request<Youth>("PATCH", `/youths/${id}`, attrs),
  destroy: (id: number) => request<void>("DELETE", `/youths/${id}`),
};

export const guardians = {
  list:   (youthId: number) =>
    request<ItemsResponse<Guardian>>("GET", `/youths/${youthId}/guardians`),
  link:   (youthId: number, attrs: { user_id?: number; relation?: string; primary?: boolean; notify_consented?: boolean; note?: string }) =>
    request<Guardian>("POST", `/youths/${youthId}/guardians`, attrs),
  unlink: (youthId: number, id: number) =>
    request<void>("DELETE", `/youths/${youthId}/guardians/${id}`),
};

export const programs = {
  list:   (opts: { status?: string; bureau_id?: number } = {}) =>
    request<ItemsResponse<Program>>("GET", `/programs${qs(opts)}`),
  get:    (id: number) => request<Program>("GET", `/programs/${id}`),
  create: (attrs: Partial<Program> & { bureau_code: BureauCode; title: string }) =>
    request<Program>("POST", "/programs", attrs),
  update: (id: number, attrs: Partial<Program>) =>
    request<Program>("PATCH", `/programs/${id}`, attrs),
  destroy: (id: number) => request<void>("DELETE", `/programs/${id}`),
  transition: (id: number, to: string) =>
    request<Program>("POST", `/programs/${id}/transition`, { to }),
};

export const participants = {
  list:   (programId: number, opts: { status?: string; role?: string } = {}) =>
    request<ItemsResponse<Participant>>("GET", `/programs/${programId}/participants${qs(opts)}`),
  apply:  (programId: number, attrs: { role?: string; youth_id?: number; user_id?: number; note?: string }) =>
    request<Participant>("POST", `/programs/${programId}/participants`, attrs),
  accept:  (programId: number, id: number) => request<Participant>("POST", `/programs/${programId}/participants/${id}/accept`),
  decline: (programId: number, id: number) => request<Participant>("POST", `/programs/${programId}/participants/${id}/decline`),
  attended:(programId: number, id: number) => request<Participant>("POST", `/programs/${programId}/participants/${id}/attended`),
  cancel:  (programId: number, id: number) => request<Participant>("POST", `/programs/${programId}/participants/${id}/cancel`),
};

export const safetyWatches = {
  list:   (opts: { status?: string; watchable_type?: string; watchable_id?: number } = {}) =>
    request<ItemsResponse<SafetyWatch>>("GET", `/safety_watches${qs(opts)}`),
  create: (attrs: { watchable_type: string; watchable_id: number; severity_hint?: string; note?: string }) =>
    request<SafetyWatch>("POST", "/safety_watches", attrs),
  pause:   (id: number) => request<SafetyWatch>("POST", `/safety_watches/${id}/pause`),
  resume:  (id: number) => request<SafetyWatch>("POST", `/safety_watches/${id}/resume`),
  end:     (id: number) => request<SafetyWatch>("POST", `/safety_watches/${id}/end`),
  approve: (id: number) => request<SafetyWatch>("POST", `/safety_watches/${id}/approve`),
};

export const motions = {
  list:   (opts: { status?: string; region_id?: number } = {}) =>
    request<ItemsResponse<Motion>>("GET", `/youth_council_motions${qs(opts)}`),
  get:    (id: number) => request<Motion>("GET", `/youth_council_motions/${id}`),
  create: (attrs: { proposer_youth_id?: number; title: string; description?: string; category?: string; region_id?: number }) =>
    request<Motion>("POST", "/youth_council_motions", attrs),
  transition: (id: number, to: string) =>
    request<Motion>("POST", `/youth_council_motions/${id}/transition`, { to }),
};

export const votes = {
  // 비운영자: { tally } 만 · 운영자: { items, tally }
  list:   (motionId: number) =>
    request<{ items?: any[]; tally: Tally }>("GET", `/youth_council_motions/${motionId}/votes`),
  cast:   (motionId: number, attrs: { youth_id?: number; choice: "yes" | "no" | "abstain"; reason?: string }) =>
    request<any>("POST", `/youth_council_motions/${motionId}/votes`, attrs),
};

export const teachers = {
  list:   (opts: { status?: string; bureau_id?: number } = {}) =>
    request<ItemsResponse<Teacher>>("GET", `/teachers${qs(opts)}`),
  apply:  (attrs: { bureau_code: BureauCode; specialty?: string; bio?: string; statement?: string; user_id?: number }) =>
    request<Teacher>("POST", "/teachers", attrs),
  approve: (id: number) => request<Teacher>("POST", `/teachers/${id}/approve`),
  suspend: (id: number) => request<Teacher>("POST", `/teachers/${id}/suspend`),
};

export const curricula = {
  list:   (opts: { status?: string; bureau_id?: number; region_id?: number } = {}) =>
    request<ItemsResponse<Curriculum>>("GET", `/curricula${qs(opts)}`),
  get:    (id: number) => request<Curriculum>("GET", `/curricula/${id}`),
  create: (attrs: { title: string; summary?: string; outline?: string; tags?: string; bureau_code?: BureauCode; min_grade?: number; max_grade?: number; session_count?: number; region_id?: number }) =>
    request<Curriculum>("POST", "/curricula", attrs),
  transition: (id: number, to: string) =>
    request<Curriculum>("POST", `/curricula/${id}/transition`, { to }),
  destroy: (id: number) => request<void>("DELETE", `/curricula/${id}`),
};

export const mapLayer = {
  resources: (category?: string) =>
    request<ItemsResponse<MapResource>>("GET", `/map/resources${qs({ category })}`),
  youths:    (opts: { track?: string } = {}) =>
    request<ItemsResponse<{ id: number; name: string; track: string; lat: number; lng: number; region_id: number | null }>>(
      "GET", `/map/youths${qs(opts)}`),
};

export const health = () => request<{ ok: boolean; module: string; enabled: boolean }>("GET", "/health");

// 한글 라벨 — 상태코드/트랙/역할 뷰 변환.
export const LABELS = {
  memberStatus: { pending: "대기", active: "활성", revoked: "해지" } as Record<string, string>,
  roleInBureau: {
    village_lead: "리더(국장급)",
    village_teacher: "마을교사",
    village_parent: "학부모 네트워크",
    village_youth: "청소년",
  } as Record<string, string>,
  youthTrack: {
    general: "일반",
    youth_council: "공의 의원",
    care_subject: "돌봄 대상",
  } as Record<string, string>,
  youthStatus: { active: "활동 중", inactive: "일시비활성", withdrawn: "탈퇴" } as Record<string, string>,
  programStatus: {
    draft: "초안", recruiting: "모집 중", in_progress: "진행 중",
    completed: "완료", archived: "보관",
  } as Record<string, string>,
  participantStatus: {
    applied: "신청", accepted: "수락", declined: "거절",
    attended: "출석", cancelled: "취소",
  } as Record<string, string>,
  participantRole: {
    participant: "참여자", teacher: "교사", mentor: "멘토", parent: "학부모",
  } as Record<string, string>,
  watchStatus: { active: "관찰", paused: "일시중지", ended: "종료" } as Record<string, string>,
  watchable: { Youth: "청소년", Region: "권역", VillageProgram: "프로그램" } as Record<string, string>,
  severity: { low: "낮음", normal: "보통", high: "주의" } as Record<string, string>,
  motionStatus: {
    draft: "초안", open: "투표 중", closed: "마감",
    adopted: "채택", rejected: "부결",
  } as Record<string, string>,
  teacherStatus: {
    pending: "심사 중", approved: "인증", suspended: "정지", expired: "만료",
  } as Record<string, string>,
  curriculumStatus: { draft: "초안", published: "발행", archived: "보관" } as Record<string, string>,
};

// 운영자 role 판정 — Village 전체에서 공유.
export const OPERATOR_ROLES = ["system_admin", "region_admin", "coordinator"] as const;
export const isOperator = (role?: string | null) =>
  !!role && (OPERATOR_ROLES as readonly string[]).includes(role);
